/**
 * BenefitGrid Organism Component
 * Grid of benefit cards with section title
 */

import React from "react";
import { storyblokEditable } from "@/lib/blocks";
import { Container } from "@/components/atoms/Container";
import { Heading } from "@/components/atoms/Heading";
import { Icon } from "@/components/atoms/Icon";
import { BenefitCard } from "@/components/molecules/BenefitCard";
import type { BenefitGridBlok } from "@/types";

export interface BenefitGridProps {
  blok: BenefitGridBlok;
}

export const BenefitGrid: React.FC<BenefitGridProps> = ({ blok }) => {
  const { section_title, section_icon, benefits, columns = 3 } = blok;

  const gridCols = {
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-2 lg:grid-cols-3",
    4: "sm:grid-cols-2 lg:grid-cols-4",
  };

  return (
    <section
      {...storyblokEditable(blok)}
      id={(blok as {anchor?: string}).anchor || undefined}
      className="scroll-mt-24 py-12 md:py-16 lg:py-20 xl:py-24 bg-theme-inset"
    >
      <Container>
        {/* Section Header */}
        <div className="flex flex-col items-start gap-4 md:gap-6 mb-4 md:mb-6">
          <div className="p-2 md:p-3 bg-theme-surface rounded-lg inline-flex">
            <Icon
              name="benefits"
              size={36}
              className="text-black dark:text-primary"
            />
          </div>
          <Heading level="h2" className="text-black dark:text-primary">
            {section_title}
          </Heading>
        </div>

        {/* Benefit Cards Grid */}
        <div
          className={`scroll-mt-24 grid grid-cols-1 ${gridCols[columns]} pt-6 lg:pt-8 gap-y-6 md:gap-y-8 lg:gap-y-10`}
        >
          {benefits &&
            benefits.map((benefit, index) => {
              const hasBorderSm = index % 2 !== 0;
              const hasBorderLg = index % Number(columns) !== 0;

              let borderClass = "";
              if (hasBorderSm && hasBorderLg) {
                borderClass = "sm:border-l-2";
              } else if (!hasBorderSm && hasBorderLg) {
                borderClass = "lg:border-l-2";
              } else if (hasBorderSm && !hasBorderLg) {
                borderClass = "sm:border-l-2 lg:border-l-0";
              }

              if (borderClass) {
                borderClass += " border-[#D1D1D6] dark:border-[#232426]";
              }

              return (
                <BenefitCard
                  key={benefit._uid}
                  {...benefit}
                  borderClass={borderClass}
                  className="sm:px-6 lg:px-8"
                />
              );
            })}
        </div>
      </Container>
    </section>
  );
};

export default BenefitGrid;
